import { useMemo, useState } from "react"
import type { ReactNode } from "react"
import { Ship } from "../App/Ship"
import { GetApp } from "../App"
import { Format } from "../App/Format"
import { GetShipContext } from "../App/Context"
import { useSubscription } from "../App/useSubscription"
import { Icon, Icons } from "../Components/Icon"
import { classNames } from "../Components"
import { IAlertThreshold, ILogbookAppNMEAData, ILogbookAppStatusResult, IN2KDirectionReference, IN2KEngine, IN2KHeading, IN2KWind, IN2KWindReference, INMEADepth, INMEAEngine, INMEAEntry, INMEAHeading, INMEAWind, INMEAWindReference } from "../Types"
import { Subscription } from "../Types/Generated/LogbookApi/Subscriptions"
import { localStorageValue } from "../App/LocalStorage"

// #1217: matches the `logbook-language` / `logbook-theme` storage-key convention.
const instrumentModesStorageKey = "logbook-instrument-modes";

interface InstrumentsViewProps {
  minimized: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  setMinimized: any;
  // #1204: whether the NMEA connection is actually live right now. Defaults to `true`
  // so demo/catalog/print fixtures that don't drive this from live status keep their
  // prior no-warning look. `ShipView` passes the real-time value through.
  connected?: boolean;
}

export interface InstrumentModes {
  [instrument: string]: string;
}

// #53: an instrument's render result, plus an optional warning/alarm severity used
// to color the instrument (only the depth instrument sets this today).
export type InstrumentSeverity = "warning" | "alarm";
export interface InstrumentRenderResult {
  content: ReactNode;
  severity?: InstrumentSeverity;
  // #1217: the entries this instrument can cycle through, and the field the stored
  // mode is compared against. Set only by instruments that support cycling; the
  // container makes the instrument clickable when there is more than one entry.
  cycle?: { entries: INMEAEntry[]; field?: string };
}
type InstrumentRenderCallback = (data: ILogbookAppNMEAData) => InstrumentRenderResult | undefined;
interface InstrumentRenderCallbacks {
  [instrument: string]: InstrumentRenderCallback;
}

// Pure selection/derivation logic for the instrument readouts. Kept free of React
// and JSX so it can be unit-tested in isolation; the component renders these values.
export const InstrumentsViewData = {
  defaultFieldCallback(e: INMEAEntry): string {
    return e.nmeaType;
  },
  // The entry matching the instrument's current mode, or the instrument's fallback
  // when no mode is set. `field` maps an entry to the value compared against the
  // stored mode; `fallback` picks the default reading (#1217: the wind instrument
  // needs a meaningful default, since the entry order is not significant).
  findMode<T extends INMEAEntry>(modes: InstrumentModes, instrument: string, data?: T[], field?: (entry: T) => string, fallback?: (data: T[]) => T | undefined): T | undefined {
    if (!data || data.length < 1) {
      return undefined;
    }
    const fieldCallback = field || InstrumentsViewData.defaultFieldCallback;
    const mode = modes[instrument];
    return mode ? data.find((d) => fieldCallback(d) == mode) : (fallback ? fallback(data) : data[0]);
  },
  // The nmeaType of the next entry after the current mode, wrapping around the list.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: replace `any` with a concrete type
  nextMode(modes: InstrumentModes, data: any[], instrument: string, field: string = "nmeaType"): string {
    const mode = modes[instrument];
    let i = (mode ? data.findIndex((d) => d[field] == mode) : 0) + 1;
    if (i >= data.length) {
      i = 0;
    }
    return data[i].nmeaType;
  },
  // Prefer the VTG (course-over-ground) heading, else the first available heading.
  selectHeading(headings?: INMEAHeading[]): INMEAHeading | undefined {
    return headings ? (headings.find((h) => h.nmeaType == "VTG") || headings[0]) : undefined;
  },
  // #1217: prefer the apparent reading, else the first available one. The wind array
  // comes from a Swift dictionary, so its order carries no meaning -- without this the
  // panel showed an arbitrary reading, routinely labelling apparent wind "True".
  selectWind(winds?: INMEAWind[]): INMEAWind | undefined {
    if (!winds || winds.length < 1) {
      return undefined;
    }
    return winds.find((w) => w.reference === INMEAWindReference.apparent) || winds[0];
  },
  // #1217: the title key for a reading -- apparent wind, true wind as a compass
  // direction, or true wind still measured from the bow.
  windKind(wind: INMEAWind): "apparent" | "true" | "trueRelative" {
    switch (wind.reference) {
      case INMEAWindReference.apparent: return "apparent";
      case INMEAWindReference.trueBoat: return "trueRelative";
      case INMEAWindReference.trueDirection: return "true";
    }
  },
  // #1217: a compass direction stays as-is; a bow-relative angle folds to 0-180 with a
  // port/starboard sense, using the same convention as `LogNavigationViewData.courseToWind`.
  // Pure -- the caller localizes the side.
  windAngle(wind: INMEAWind): { angle: number; side?: "port" | "starboard"; compass: boolean } {
    if (wind.reference === INMEAWindReference.trueDirection) {
      return { angle: wind.angle, side: undefined, compass: true };
    }
    const relative = ((wind.angle % 360) + 360) % 360;
    const angle = relative > 180 ? 360 - relative : relative;
    const side = angle === 0 || angle === 180 ? undefined : (relative > 180 ? "port" : "starboard");
    return { angle, side, compass: false };
  },
  // #1217: the cycled instrument modes are persisted, so a selected reading survives a
  // reload. Storage is user-editable, so anything unexpected degrades to "no modes".
  parseInstrumentModes(raw?: string | null): InstrumentModes {
    if (!raw) {
      return {};
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return {};
      }
      const modes: InstrumentModes = {};
      for (const [instrument, mode] of Object.entries(parsed)) {
        if (typeof mode === "string") {
          modes[instrument] = mode;
        }
      }
      return modes;
    } catch {
      return {};
    }
  },
  serializeInstrumentModes(modes: InstrumentModes): string {
    return JSON.stringify(modes);
  },
  // The displayed course of a heading: magnetic when present, else true.
  headingCourse(heading?: INMEAHeading): number | undefined {
    return heading ? (heading.courseMagnetic || heading.courseTrue) : undefined;
  },
  // Restrict the engine readouts to the ship's configured engine numbers when known.
  filterEngines(engines?: INMEAEngine[], configured?: Set<number>): INMEAEngine[] | undefined {
    return engines && configured ? engines.filter((e) => configured.has(e.number)) : engines;
  },
  // The `rpm | rpm` readout, engines sorted by number.
  engineRpmText(engines: INMEAEngine[]): string {
    return engines.toSorted((a, b) => a.number - b.number).map((engine) => `${engine.rpm.toFixed(0)}`).join(" | ");
  },
  // #1027 (tracker #976): N2K fallbacks, used by the render callbacks only when no 0183 reading
  // exists for the instrument. N2K has no VTG/type concept, so these just take the
  // first entry rather than mode-select or sub-type filter.
  n2kHeadingCourse(headings?: IN2KHeading[]): { course: number; magnetic: boolean } | undefined {
    const h = headings?.[0];
    return h ? { course: h.heading, magnetic: h.headingReference === IN2KDirectionReference.magnetic } : undefined;
  },
  // Remap N2K's `instance`/`speed` fields to the 0183 `number`/`rpm` shape so
  // filterEngines/engineRpmText can be reused unchanged.
  n2kEngines(engines?: IN2KEngine[]): INMEAEngine[] | undefined {
    return engines?.map((e) => ({
      receivedAt: e.receivedAt, source: e.source, nmeaType: e.nmeaType,
      number: e.instance ?? 0, rpm: e.speed,
    }));
  },
  // Remap N2K wind entries to the 0183 shape. The N2K reference maps onto the same
  // three-way `INMEAWindReference` the 0183 parser produces (#1217), so both protocols
  // feed identical selection, cycling and formatting.
  n2kWinds(winds?: IN2KWind[]): INMEAWind[] | undefined {
    return winds?.map((w) => {
      const reference = w.reference === IN2KWindReference.trueNorth || w.reference === IN2KWindReference.magneticNorth
        ? INMEAWindReference.trueDirection
        : w.reference === IN2KWindReference.trueBoat || w.reference === IN2KWindReference.trueWater
          ? INMEAWindReference.trueBoat
          : INMEAWindReference.apparent;
      return {
        receivedAt: w.receivedAt, source: w.source, nmeaType: w.nmeaType, reference,
        priority: reference === INMEAWindReference.trueDirection,
        relative: reference === INMEAWindReference.apparent,
        angle: w.angle, speed: w.speed,
      };
    });
  },
  // #53: general-purpose warning/alarm severity for an instrument reading against a
  // ship-configured threshold. "Lower is worse", mirroring DepthConsumer.swift's
  // comparison direction: at/below `alarm` -> alarm, at/below `warning` -> warning,
  // else no severity.
  severity(value?: number, threshold?: IAlertThreshold): "warning" | "alarm" | undefined {
    if (value === undefined || threshold === undefined) {
      return undefined;
    }
    if (value <= threshold.alarm) {
      return "alarm";
    }
    if (value <= threshold.warning) {
      return "warning";
    }
    return undefined;
  },
};

export function InstrumentsView({ minimized, setMinimized, connected = true }: InstrumentsViewProps) {
  const app = GetApp()!;
  const t = app.getT("InstrumentsView");
  const { ship } = GetShipContext();

  const configuredEngineNumbers = useMemo(() => {
    if (!ship) return undefined;
    const engines = Ship.engines(ship);
    if (!engines || engines.length === 0) return undefined;
    return new Set(engines.map((e, i) => e.number ?? i));
  }, [ship]);

  // #53: depth thresholds only matter (and are only ever read) when the coloring
  // feature is on, mirroring the DepthWatchView precedent for reading ship settings.
  const depthThreshold = useMemo(
    () => (ship ? Ship.shipSettings(ship).thresholds?.depth : undefined),
    [ship],
  );

  const [nmeaData, setNmeaData] = useState<ILogbookAppNMEAData | undefined>(app.status?.nmea?.data);
  // #1217: the cycled reading per instrument, restored from and written back to local
  // storage so a user's choice survives a reload.
  const [instrumentModes, setInstrumentModes] = useState<InstrumentModes>(
    () => InstrumentsViewData.parseInstrumentModes(localStorageValue(instrumentModesStorageKey)),
  );

  function cycleInstrument(instrument: string, entries: INMEAEntry[], field?: string) {
    setInstrumentModes((modes) => {
      const updated = { ...modes, [instrument]: InstrumentsViewData.nextMode(modes, entries, instrument, field) };
      localStorage.setItem(instrumentModesStorageKey, InstrumentsViewData.serializeInstrumentModes(updated));
      return updated;
    });
  }

  useSubscription((status: ILogbookAppStatusResult, _type: string) => {
    if (status?.nmea?.data) {
      setNmeaData(status.nmea.data);
    }
  }, Subscription.AppStatus);

  function toggleMinimized() {
    setMinimized(!minimized);
  }

  const renderInstrument: InstrumentRenderCallbacks = {
    compass: function (data: ILogbookAppNMEAData) {
      const heading = InstrumentsViewData.selectHeading(data.heading);
      const course = InstrumentsViewData.headingCourse(heading);
      const n2kHeading = !heading ? InstrumentsViewData.n2kHeadingCourse(data.n2k?.heading) : undefined;
      const displayCourse = course !== undefined ? course : n2kHeading?.course;
      return {
        content: (
          <>
            <div className="InstrumentsView-instrument-title">
              {heading
                ? (t("compass_title_" + (heading.courseMagnetic ? "magnetic" : "true")) || t("compass_title"))
                : (n2kHeading ? (t("compass_title_" + (n2kHeading.magnetic ? "magnetic" : "true")) || t("compass_title")) : t("compass_title"))}
            </div>
            <div className="InstrumentsView-instrument-text">
              <span className="value">{displayCourse !== undefined ? Format.course(displayCourse) : "–"}</span>
            </div>
          </>
        ),
      };
    },
    course: function (data: ILogbookAppNMEAData) {
      const navigation = data.navigation;
      const track = (navigation && navigation.track) ? navigation.track : data.n2k?.courseSpeed?.cog;
      return {
        content: (
          <>
            <div className="InstrumentsView-instrument-title">
              {t("course_title")}
            </div>
            <div className="InstrumentsView-instrument-text">
              <span className="value">{track ? Format.course(track) : "–"}</span>
            </div>
          </>
        ),
      };
    },
    depth: function (data: ILogbookAppNMEAData) {
      const depth = InstrumentsViewData.findMode<INMEADepth>(instrumentModes, "depth", data.depth);
      const n2kDepth = !depth ? data.n2k?.depth?.[0] : undefined;
      const meters = depth ? depth.meters : n2kDepth?.depth;
      // #53: unconditional on any active anchor/depth watch — only gated behind the
      // anchor-alerts feature flag and the ship having configured depth thresholds.
      const severity = InstrumentsViewData.severity(meters, depthThreshold);
      return {
        content: (
          <>
            <div className="InstrumentsView-instrument-title">
              {depth ? `${t("depth_title")} - ${t("depth_title_" + depth.type)}` : t("depth_title")}
            </div>
            <div className="InstrumentsView-instrument-text">
              <span className="value">{meters !== undefined ? meters.toFixed(1) : "–"}</span>
              <span className="unit">m</span>
            </div>
          </>
        ),
        severity,
        cycle: data.depth ? { entries: data.depth } : undefined,
      };
    },
    engines: function (data: ILogbookAppNMEAData) {
      const engine = InstrumentsViewData.filterEngines(data.engine, configuredEngineNumbers)
        ?? InstrumentsViewData.filterEngines(InstrumentsViewData.n2kEngines(data.n2k?.engine), configuredEngineNumbers);
      return {
        content: (
          <>
            <div className="InstrumentsView-instrument-title">
              {engine ? t(engine.length < 2 ? "engine_title_single" : "engine_title_multiple") : t("engine_title_single")}
            </div>
            <div className="InstrumentsView-instrument-text">
              <span className="value">{engine ? InstrumentsViewData.engineRpmText(engine) : "–"}</span>
            </div>
          </>
        ),
      };
    },
    speed: function (data: ILogbookAppNMEAData) {
      const navigation = data.navigation;
      const sog = (navigation && navigation.sog !== undefined) ? navigation.sog : data.n2k?.courseSpeed?.sog;
      return {
        content: (
          <>
            <div className="InstrumentsView-instrument-title">{t("speed_title")}</div>
            <div className="InstrumentsView-instrument-text">
              <span className="value">{sog !== undefined ? sog.toFixed(1) : "–"}</span>
              <span className="unit">kn</span>
            </div>
          </>
        ),
      };
    },
    wind: function (data: ILogbookAppNMEAData) {
      // #1217: cycling keys on `nmeaType`, which now distinguishes all three readings,
      // and the default is the apparent one rather than an arbitrary array position.
      const winds = data.wind?.length ? data.wind : InstrumentsViewData.n2kWinds(data.n2k?.wind);
      const wind = InstrumentsViewData.findMode<INMEAWind>(instrumentModes, "wind", winds, undefined, InstrumentsViewData.selectWind);
      const angle = wind ? InstrumentsViewData.windAngle(wind) : undefined;
      const side = angle?.side ? ` ${t("wind_side_" + angle.side)}` : "";
      const bearing = angle ? (angle.compass ? Format.course(angle.angle) : `${Format.angle(angle.angle)}${side}`) : undefined;
      return {
        content: (
          <>
            <div className="InstrumentsView-instrument-title">
              {wind ? `${t("wind_title")} - ${t("wind_title_" + InstrumentsViewData.windKind(wind))}` : t("wind_title")}
            </div>
            <div className="InstrumentsView-instrument-text">
              <span className="value">{wind && bearing ? `${bearing} | ${wind.speed.toFixed(1)}` : "–"}</span>
              <span className="unit">kn</span>
            </div>
          </>
        ),
        cycle: winds ? { entries: winds } : undefined,
      };
    },
  };
  const instrumentOrder = ["compass", "depth", "course", "speed", "engines", "wind"];
  const _instrumentData = { "course": "heading", "depth": "depth" };

  const instruments = [];
  for (const instrument of instrumentOrder) {
    const result = nmeaData && renderInstrument[instrument] && renderInstrument[instrument](nmeaData);
    // #1217: only offer cycling when there is genuinely more than one reading to reach.
    const cycle = result?.cycle && result.cycle.entries.length > 1 ? result.cycle : undefined;
    instruments.push(
      <div
        key={instrument}
        className={classNames([
          "InstrumentsView-instrument",
          "instrument-" + instrument,
          cycle && "InstrumentsView-instrument-cycle",
          result?.severity && `InstrumentsView-instrument-${result.severity}`,
        ])}
        role={cycle ? "button" : undefined}
        tabIndex={cycle ? 0 : undefined}
        onClick={cycle ? () => cycleInstrument(instrument, cycle.entries, cycle.field) : undefined}
      >
        {result?.content}
      </div>,
    );
  }

  return (
    <div className={"InstrumentsView" + (minimized ? " minimized" : "")}>
      {!connected && (
        <Icon icon={Icons.Alert} className="InstrumentsView-warning" title={t("nmea_lost_title")} />
      )}
      <Icon icon={minimized ? Icons.ArrowsUp : Icons.ArrowsDown} className="InstrumentsView-minimize" onClick={toggleMinimized} />
      <div className="InstrumentsView-fill pre"></div>
      <div className="InstrumentsView-instruments">
        {instruments}
      </div>
      <div className="InstrumentsView-fill post">
      </div>
    </div>
  );
}
